/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useRecoilState, useSetRecoilState } from "recoil";
import { NoteAPI } from "../../../api/Note";
import { NoteCategoriesAPI } from "../../../api/NoteCategory";
import { ContactNotesAtom, NoteCategoriesAtom } from "../../../atoms/NotesAtom";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { SpaceBetween } from "../../../styles/general";
import { IContact } from "../../../types/Contact";
import { INoteCategory, INoteType } from "../../../types/NoteCategory";
import { ContactNote, INoteTrigger } from "../../../types/NoteTypes";
import IconBerserkerMail from "../../Icon";
import CreateUserNote from "../../Modal/container/CreateUserNote";
import { useModal } from "../../Modal/Modal";
import FilterNotes from "../../Notes/FilterNotes";
import SearchInput from "../../SearchInput";
import StyledButton from "../../StyledButton";
import { TableFooter } from "../../StyledTable";
import { EmptyContainer, SkeletonCard } from "./Emails";

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  cursor: default;
`;

const RowContainer = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: 240px 180px 1fr;
  grid-column-gap: 20px;
  width: 100%;
  background: #f8f8f8 0% 0% no-repeat padding-box;
  border-radius: 2px;
  margin-bottom: 10px;

  :last-child {
    margin-bottom: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Between = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
`;

interface CircleProps {
  user: boolean;
}

const Circle = styled.div<CircleProps>`
  display: grid;
  align-items: center;
  border: 1px solid;
  border-color: ${(props) => (props.user ? "#FB972E" : "#868482")};
  background-color: #fff;
  height: 40px;
  width: 40px;
  border-radius: 100%;

  flex-shrink: 0;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;

  .title {
    margin-bottom: 5px;
  }

  .info {
    display: flex;
    align-items: center;
    width: 80px;
  }

  .text {
    font-weight: 400;
  }
`;

const PopRow = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;

  :last-child {
    margin-bottom: 0;
  }

  .title-pop {
    margin-right: 5px;
    font-weight: 600;
  }
`;

const Text = styled.div`
  color: #333333;
  font-size: 14px;

  white-space: nowrap;
`;

const SubTitle = styled.div`
  color: #333333;
  font-size: 16px;
  font-weight: 600;
`;

const InfoContainer = styled.div`
  position: relative;
  margin-left: 5px;
  cursor: pointer;
  padding-right: 10px;

  :hover {
    svg {
      path {
        fill: #fb972e;
      }
    }
  }
`;

const Popover = styled.div`
  position: absolute;
  z-index: 999;
  height: fit-content;
  width: fit-content;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  display: flex;
  flex-direction: column;
  padding: 20px;
  left: 20px;
  top: 0px;
  background-color: #fff;

  > div {
    margin-bottom: 10px;
  }
`;

interface SubjectProps {
  isEllipsis: boolean;
}

const Subject = styled.div<SubjectProps>`
  color: #333333;
  font-size: 16px;

  > p {
    ${(props) =>
      props.isEllipsis &&
      css`
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        display: inline-block;
        max-width: calc(100vw - 900px);
      `}
  }

  > p > strong {
    white-space: nowrap;
  }
`;

const ButtonShow = styled.div`
  color: #333333;
  font-size: 16px;
  font-weight: 600;
  width: 120px;
  margin-left: 10px;
  display: inline-block;
  cursor: pointer;
  vertical-align: bottom;

  :hover {
    opacity: 0.7;
  }
`;

const PopoverTitle = styled.div`
  color: #888888;
  font-size: 12px;
`;

const noteTitle = css`
  margin-bottom: 20px;
  font-size: 18px;
`;
interface Props {
  contact: IContact;
}

export enum DateFilter {
  Recent = "recent",
  Older = "older",
}
export interface FilterNote {
  categories: INoteCategory[];
  type: INoteType[];
  date: DateFilter;
}

const TRIGGER_MAP: { [key in INoteTrigger]: string } = {
  [INoteTrigger.CONTACT_CREATED]: "Contact Created",
  [INoteTrigger.TAG_APPLIED]: "Applied Tag",
  [INoteTrigger.TAG_REMOVED]: "Removed Tag",
  [INoteTrigger.USER_NOTE]: "User Note",
  [INoteTrigger.FORM_SUBMIT]: "Form Submit",
  [INoteTrigger.STATUS]: "Updated Status",
};

export default function Notes(props: Props) {
  const [notes, setNotes] = useRecoilState(ContactNotesAtom);
  const [filteredNotes, setFilteredNotes] = useState<ContactNote[]>([]);
  const setCategories = useSetRecoilState(NoteCategoriesAtom);
  const [infoFilter, setInfoFilter] = useState("");
  const modal = useModal();
  const [filter, setFilter] = useState<FilterNote>({
    categories: [],
    type: [],
    date: DateFilter.Recent,
  });
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.NOTES
  );

  useEffect(() => {
    (async () => {
      try {
        const newNotes = await NoteAPI.getNotesFromContact(props.contact._id);
        if (!newNotes) return;

        setNotes(newNotes);
      } catch (err) {
        console.log("err", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const categoriesResponse = await NoteCategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);
        if (categoriesResponse.length < 1) {
          setIsEmpty(true);
        }
      }
    })();
  }, []);

  useEffect(() => {
    setFilteredNotes(getFilteredNotes());
  }, [notes, filter, infoFilter]);

  function getFilteredNotes() {
    if (!notes) return [];

    let notesWithFilter: ContactNote[] = [...notes];

    if (filter.type.length > 0) {
      notesWithFilter = notesWithFilter.filter((not) => {
        if (filter.type.find((t) => t === not.category.type)) return true;
        return false;
      });
    }

    if (filter.categories.length > 0) {
      notesWithFilter = notesWithFilter.filter((not) => {
        if (filter.categories.find((c) => c._id === not.category._id))
          return true;
        return false;
      });
    }

    return notesWithFilter
      .filter((not) =>
        not.text.trim().toLowerCase().includes(infoFilter.toLowerCase().trim())
      )
      .sort((a, b) => {
        if (filter.date === DateFilter.Recent)
          return dayjs(b.createdAt).diff(a.createdAt);
        else return dayjs(a.createdAt).diff(b.createdAt);
      });
  }

  function handlePage(vle: number) {
    setPage(vle);
  }

  if (loading)
    return (
      <NotesContainer>
        <Between>
          <SubTitle css={noteTitle}>Notes</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for notes"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            <FilterNotes
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
          </Buttons>
        </Between>

        {Array(5)
          .fill(0)
          .map((e) => (
            <SkeletonCard
              css={css`
                width: 100%;
                height: 80px;
                margin-bottom: 10px;
              `}
            />
          ))}
      </NotesContainer>
    );

  if (isEmpty) {
    return getEmptyContainer();
  }

  function getEmptyContainer() {
    let textToShow: string;

    if (infoFilter) {
      textToShow = "No notes matching your criteria";
    } else {
      if (filter.type.length) {
        textToShow = "No notes matching your criteria";
      } else {
        textToShow = "No notes to show";
      }
    }

    return (
      <EmptyContainer>
        <IconBerserkerMail name="note-empty" />
        <SubTitle
          css={css`
            margin-top: 15.8px;
            font-size: 16px;
          `}
        >
          {textToShow}
        </SubTitle>
      </EmptyContainer>
    );
  }

  return (
    <NotesContainer>
      {(filteredNotes.length > 0 || infoFilter || filter) && (
        <Between>
          <SubTitle css={noteTitle}>Notes</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for notes"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            <FilterNotes
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
            <StyledButton
              css={css`
                margin-left: 15px;
                height: 40px;
              `}
              onClick={() => {
                modal.pushModal(
                  <CreateUserNote contactId={props.contact._id} />,
                  "Add Note"
                );
              }}
            >
              Add Note
            </StyledButton>
          </Buttons>
        </Between>
      )}

      {filteredNotes.length > 0 ? (
        <>
          {filteredNotes
            .slice(
              page * (cachedRowsPerPage || rowsPerPage),
              page * (cachedRowsPerPage || rowsPerPage) +
                (cachedRowsPerPage || rowsPerPage)
            )
            .map((n: ContactNote, index) => {
              const note: ContactNote = {
                ...n,
                text: n.text,
              };
              return <NoteRow note={note} key={n._id} />;
            })}
          <TableFooter
            isSmaller
            page={page}
            length={filteredNotes.length || 0}
            rowsPerPage={cachedRowsPerPage || rowsPerPage}
            setRowsPerPage={(vle: number) => {
              setRowsPerPage(vle);
              updateCachedRowsPerPage(vle);
            }}
            handlePage={handlePage}
          />
        </>
      ) : (
        getEmptyContainer()
      )}
    </NotesContainer>
  );
}

function NoteRow(props: { note: ContactNote }) {
  const { note } = props;

  const [showMore, setShowMore] = useState(true);
  const [isLarge, setIsLarge] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);

  return (
    <RowContainer>
      <Row>
        <Circle user={note.category.type === INoteType.USER}>
          <IconBerserkerMail
            name="note"
            color={note.category.type === INoteType.USER && "#FB972E"}
          />
        </Circle>
        <Column>
          <Text className="title">
            {dayjs(note?.createdAt).format("MMM D, YYYY [@] h:mm a (z)")}
          </Text>
          <SubTitle>{TRIGGER_MAP[note.trigger]}</SubTitle>
        </Column>
      </Row>

      <Row>
        <Column>
          <Text className="title">Category</Text>
          <SubTitle>{note.category.name}</SubTitle>
        </Column>
      </Row>

      <Row
        css={css`
          display: block;
        `}
      >
        <Column>
          <Text className="title info">
            Notes
            <InfoContainer
              onMouseEnter={() => setOpenInfo(true)}
              onMouseLeave={() => setOpenInfo(false)}
            >
              <IconBerserkerMail name="info" />

              {openInfo && (
                <Popover>
                  <PopoverTitle>INFO</PopoverTitle>

                  {note.deviceId === "system" ? (
                    <PopRow>
                      <Text className="title-pop">From:</Text>
                      <Text>System</Text>
                    </PopRow>
                  ) : (
                    <>
                      {note?.device !== "" && (
                        <PopRow>
                          <Text className="title-pop">Device:</Text>
                          <Text>{note.device}</Text>
                        </PopRow>
                      )}
                      {note?.os !== "" && (
                        <PopRow>
                          <Text className="title-pop">OS:</Text>
                          <Text>{note.os}</Text>
                        </PopRow>
                      )}
                      {note?.browser !== "" && (
                        <PopRow>
                          <Text className="title-pop">Browser:</Text>
                          <Text>{note.browser}</Text>
                        </PopRow>
                      )}
                      <PopRow>
                        <Text className="title-pop">Device ID:</Text>
                        <Text>{note.deviceId}</Text>
                      </PopRow>
                      <PopRow>
                        <Text className="title-pop">IP Address:</Text>
                        <Text>{note.ip}</Text>
                      </PopRow>
                    </>
                  )}
                </Popover>
              )}
            </InfoContainer>
          </Text>
          <SpaceBetween>
            <Subject isEllipsis={showMore} id={"ellipse-component" + note._id}>
              <ReactMarkdown>{note.text}</ReactMarkdown>
              {isLarge && !showMore && (
                <ButtonShow
                  onClick={(e) => {
                    setShowMore(!showMore);
                    e.stopPropagation();
                  }}
                >
                  {"Show less"}
                </ButtonShow>
              )}
            </Subject>
            {isLarge && showMore && (
              <ButtonShow
                onClick={() => {
                  setShowMore(!showMore);
                }}
              >
                {"Show more"}
              </ButtonShow>
            )}
          </SpaceBetween>
        </Column>
      </Row>
    </RowContainer>
  );
}
